import React, { InputHTMLAttributes, forwardRef } from 'react';
import { cn } from '../../utils/cn';

export interface CheckboxProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'type' | 'size'> {
  label?: string;
  description?: string;
  error?: string;
  size?: 'sm' | 'md' | 'lg';
}

export const Checkbox = forwardRef<HTMLInputElement, CheckboxProps>(
  (
    {
      label,
      description,
      error,
      size = 'md',
      className,
      disabled,
      ...props
    },
    ref
  ) => {
    const sizeClasses: Record<string, string> = {
      sm: 'h-4 w-4',
      md: 'h-[18px] w-[18px]',
      lg: 'h-5 w-5',
    };

    const hasError = !!error;

    return (
      <div className="w-full">
        <div className="flex items-start">
          <div className="flex items-center h-5">
            <input
              ref={ref}
              type="checkbox"
              disabled={disabled}
              className={cn(
                'rounded border transition-all duration-150',
                'focus:ring-2 focus:ring-primary-200 focus:ring-offset-1',
                'disabled:cursor-not-allowed disabled:opacity-50',
                hasError
                  ? 'border-error-500 text-error-500 focus:ring-error-100'
                  : 'border-monday-border text-primary-500',
                'cursor-pointer',
                sizeClasses[size],
                className
              )}
              {...props}
            />
          </div>

          {(label || description) && (
            <div className="ml-2.5 flex-1">
              {label && (
                <label
                  className={cn(
                    'block text-sm font-medium',
                    hasError ? 'text-error-500' : 'text-monday-text',
                    disabled && 'opacity-50 cursor-not-allowed',
                    !disabled && 'cursor-pointer'
                  )}
                >
                  {label}
                </label>
              )}
              {description && (
                <p className={cn('text-xs mt-0.5', hasError ? 'text-error-500' : 'text-monday-text-muted', disabled && 'opacity-50')}>
                  {description}
                </p>
              )}
            </div>
          )}
        </div>

        {error && (
          <p className="mt-1 text-xs text-error-500 ml-7">{error}</p>
        )}
      </div>
    );
  }
);

Checkbox.displayName = 'Checkbox';
